// Sugere ajustes de equilíbrio de gênero entre os times em quadra (modo gênero-restrito).
// Versão pura da lógica que antes abria popup no generateTeam.
// A troca escolhe os jogadores que deixam os dois times com pontos mais próximos.
// Retorna null se não houver ajuste, ou { headline, moves[], setTeamOps[], priorityIds[] }.

const teamLabel = (id) => `Time ${id === 1 ? 'A' : 'B'}`;
const pts = (p) => p.totalPoints ?? (p.wins - p.losses);
const sumPts = (arr) => arr.reduce((s, p) => s + pts(p), 0);

// Escolhe a melhor troca simples (1 quem sai da quadra + 1 quem entra da fila + 1 quem cede a vaga)
// minimizando |base + 2*pts(quemSai) - pts(quemCede) - pts(quemEntra)|.
const pickBestSwap = (outgoing, incoming, givingUp, base) => {
  const gCands = givingUp.length ? givingUp : [null];
  let best = { o: outgoing[0], i: incoming[0], g: gCands[0] };
  let bestDiff = Infinity;
  outgoing.forEach((o) => incoming.forEach((i) => gCands.forEach((g) => {
    const diff = Math.abs(base + (2 * pts(o)) - (g ? pts(g) : 0) - pts(i));
    if (diff < bestDiff) {
      bestDiff = diff;
      best = { o, i, g };
    }
  })));
  return best;
};

// Sugere correções quando o jogo ficou desequilibrado em pontos (independente de gênero).
// Retorna { suggestion1, suggestion2 } ou null.
// Cada sugestão: { moves[], resultA, resultB, setTeamOps[], priorityIds[] }.
export function suggestImbalanceFixes({ players, targetTeamId, nextTeamIds = [] }) {
  const isSetter = (p) => p.playerType === 'Setter';

  const opposingTeamId = targetTeamId === 1 ? 2 : 1;
  const opposing = players.filter((p) => p.teamId === opposingTeamId);
  const targetExisting = players.filter((p) => p.teamId === targetTeamId);
  const selectedSet = new Set(nextTeamIds);
  const nextTeamPlayers = nextTeamIds
    .map((id) => players.find((p) => p.playerId === id))
    .filter(Boolean);
  const queue = players
    .filter((p) => p.teamId === 0 && p.checkedIn && !selectedSet.has(p.playerId));

  const targetSide = [...targetExisting, ...nextTeamPlayers];
  const targetSetters = targetSide.filter(isSetter).length;
  const opposingSetters = opposing.filter(isSetter).length;

  // Troca entre os dois times (sug. 1). Mesmo gênero sempre.
  // Setter só troca com não-setter se ninguém ficar sem levantador nem com 2.
  const canSwapTeams = (outP, inP) => {
    if (outP.gender !== inP.gender) return false;
    if (isSetter(outP) === isSetter(inP)) return true; // setter<->setter ou não<->não
    if (isSetter(outP)) return targetSetters >= 2 && opposingSetters === 0;
    return opposingSetters >= 2 && targetSetters === 0;
  };

  // Substituição no time alvo (sug. 2). Mesmo gênero sempre.
  const canSubstitute = (bench, sub) => {
    if (bench.gender !== sub.gender) return false;
    if (isSetter(bench) === isSetter(sub)) return true;
    if (isSetter(bench)) return targetSetters >= 2; // pode perder 1 setter
    return targetSetters === 0; // só ganha setter se não tinha nenhum
  };

  const targetTotal = sumPts(targetExisting) + sumPts(nextTeamPlayers);
  const opposingTotal = sumPts(opposing);
  const diff0 = targetTotal - opposingTotal;
  const toAB = (tTotal, oTotal) => (targetTeamId === 1
    ? { resultA: tTotal, resultB: oTotal }
    : { resultA: oTotal, resultB: tTotal });

  const out = { suggestion1: null, suggestion2: null, suggestion3: null };

  // Monta o resultado de uma troca entre os times (1 ou mais pares).
  const buildSwapResult = (chosen) => {
    const outIds = new Set(chosen.map((s) => s.out.playerId));
    const tTotal = targetTotal + chosen.reduce((acc, s) => acc - pts(s.out) + pts(s.in), 0);
    const oTotal = opposingTotal + chosen.reduce((acc, s) => acc + pts(s.out) - pts(s.in), 0);
    return {
      moves: chosen.flatMap((s) => [
        `${s.in.playerName} vem do ${teamLabel(opposingTeamId)} para o ${teamLabel(targetTeamId)}`,
        `${s.out.playerName} vai para o ${teamLabel(opposingTeamId)}`,
      ]),
      ...toAB(tTotal, oTotal),
      setTeamOps: [
        ...chosen.map((s) => ({ playerId: s.in.playerId, teamId: targetTeamId })),
        ...chosen.map((s) => ({ playerId: s.out.playerId, teamId: opposingTeamId })),
        ...nextTeamPlayers
          .filter((p) => !outIds.has(p.playerId))
          .map((p) => ({ playerId: p.playerId, teamId: targetTeamId })),
      ],
      priorityIds: [],
    };
  };

  // Trocas de 1 par válidas com o outro time.
  if (opposing.length > 0 && targetSide.length > 0) {
    const singleSwaps = [];
    targetSide.forEach((o) => opposing.forEach((inp) => {
      if (!canSwapTeams(o, inp)) return;
      singleSwaps.push({
        out: o,
        in: inp,
        delta: -(2 * pts(o)) + (2 * pts(inp)),
        setterMove: isSetter(o) !== isSetter(inp),
      });
    }));

    // Sugestão 1: melhor troca de 1 par.
    let best1 = null;
    let best1Diff = Math.abs(diff0);
    singleSwaps.forEach((s) => {
      const nd = Math.abs(diff0 + s.delta);
      if (nd < best1Diff) { best1Diff = nd; best1 = [s]; }
    });
    if (best1) out.suggestion1 = buildSwapResult(best1);

    // Sugestão 2: melhor troca de 2 pares (sem mover setter) — só se melhora sobre a de 1 par.
    const stable = singleSwaps.filter((s) => !s.setterMove);
    let best2 = null;
    let best2Diff = Math.abs(diff0);
    for (let i = 0; i < stable.length; i += 1) {
      for (let j = i + 1; j < stable.length; j += 1) {
        const a = stable[i];
        const b = stable[j];
        const distinct = a.out.playerId !== b.out.playerId && a.in.playerId !== b.in.playerId;
        if (distinct) {
          const nd = Math.abs(diff0 + a.delta + b.delta);
          if (nd < best2Diff) { best2Diff = nd; best2 = [a, b]; }
        }
      }
    }
    if (best2 && (!best1 || best2Diff < best1Diff)) out.suggestion2 = buildSwapResult(best2);
  }

  // Sugestão 3: alguém da seleção espera mais uma (com prioridade) e entra um substituto da fila.
  if (queue.length > 0 && nextTeamPlayers.length > 0) {
    let best = null;
    let bestScore = null;
    nextTeamPlayers.forEach((bench) => queue.forEach((sub) => {
      if (!canSubstitute(bench, sub)) return; // substituto do mesmo gênero; setter só com setter
      const tTotal = targetTotal - pts(bench) + pts(sub);
      const d = Math.abs(tTotal - opposingTotal);
      // Menor diferença primeiro; empate -> banca quem esperou menos (waitingFor menor).
      if (!best || d < bestScore.d || (d === bestScore.d && bench.waitingFor < bestScore.wait)) {
        best = { bench, sub };
        bestScore = { d, wait: bench.waitingFor };
      }
    }));
    if (best) {
      const tTotal = targetTotal - pts(best.bench) + pts(best.sub);
      out.suggestion3 = {
        moves: [
          `${best.bench.playerName} espera mais uma e recebe prioridade de próxima`,
          `${best.sub.playerName} entra no lugar (deixa mais equilibrado)`,
        ],
        ...toAB(tTotal, opposingTotal),
        setTeamOps: [
          ...nextTeamPlayers
            .filter((p) => p.playerId !== best.bench.playerId)
            .map((p) => ({ playerId: p.playerId, teamId: targetTeamId })),
          { playerId: best.sub.playerId, teamId: targetTeamId },
        ],
        priorityIds: [best.bench.playerId],
      };
    }
  }

  return out.suggestion1 || out.suggestion2 || out.suggestion3 ? out : null;
}

export function suggestBalanceAdjustments({
  players,
  femalesPerTeam = 1,
  genderBalanceMode = false,
  genderRestrictedMode = false,
  setterRestrictedMode = false,
  targetTeamId,
  nextTeamIds = [],
}) {
  if (!genderBalanceMode || !genderRestrictedMode) return null;

  const opposingTeamId = targetTeamId === 1 ? 2 : 1;
  const opposingTeam = players.filter((p) => p.teamId === opposingTeamId);
  const targetExisting = players.filter((p) => p.teamId === targetTeamId);
  const playersWaiting = players.filter((p) => p.teamId === 0 && p.checkedIn);
  const selectedIds = new Set(nextTeamIds);
  const nextTeamPlayers = nextTeamIds
    .map((id) => players.find((p) => p.playerId === id))
    .filter(Boolean);

  // base = pontos (alvo + selecionados) - pontos (time em quadra), antes da troca.
  const base = sumPts(targetExisting) + sumPts(nextTeamPlayers) - sumPts(opposingTeam);

  const opposingFemaleCount = opposingTeam.filter((p) => p.gender === 'F').length;
  const missing = femalesPerTeam - opposingFemaleCount;
  const excess = opposingFemaleCount - femalesPerTeam;

  const buildResult = (headline, malesOrFemalesOut, addedFromQueue, givingUp, playersToEnter) => ({
    headline,
    moves: [
      ...malesOrFemalesOut.map((m) => `${m.playerName} sai do ${teamLabel(opposingTeamId)} e vai para o ${teamLabel(targetTeamId)}`),
      ...addedFromQueue.map((f) => `${f.playerName} entra no ${teamLabel(opposingTeamId)} (da fila)`),
      ...givingUp.map((g) => `${g.playerName} cede a vaga e recebe prioridade`),
    ],
    setTeamOps: [
      ...playersToEnter.map((p) => ({ playerId: p.playerId, teamId: targetTeamId })),
      ...malesOrFemalesOut.map((p) => ({ playerId: p.playerId, teamId: targetTeamId })),
      ...addedFromQueue.map((p) => ({ playerId: p.playerId, teamId: opposingTeamId })),
    ],
    priorityIds: givingUp.map((p) => p.playerId),
  });

  // Caso 1: o time em quadra (oposto) precisa de mulher.
  if (missing > 0) {
    const femalesInQueue = playersWaiting.filter((p) => p.gender === 'F' && !selectedIds.has(p.playerId));
    if (femalesInQueue.length === 0) return null;
    const opposingMales = opposingTeam.filter((p) => p.gender !== 'F');
    const swappableMales = setterRestrictedMode
      ? opposingMales.filter((p) => p.playerType !== 'Setter')
      : opposingMales;
    if (swappableMales.length === 0) return null;

    const count = Math.min(missing, swappableMales.length, femalesInQueue.length);
    const malesInSelected = nextTeamPlayers.filter((p) => p.gender !== 'F');

    let malesToMove;
    let femalesToAdd;
    let givingUp;
    if (count === 1) {
      const best = pickBestSwap(swappableMales, femalesInQueue, malesInSelected, base);
      malesToMove = [best.o];
      femalesToAdd = [best.i];
      givingUp = best.g ? [best.g] : [];
    } else {
      malesToMove = swappableMales.slice(0, count);
      femalesToAdd = femalesInQueue.slice(0, count);
      givingUp = malesInSelected.slice(-count);
    }

    const givingUpIds = new Set(givingUp.map((p) => p.playerId));
    const playersToEnter = nextTeamPlayers.filter((p) => !givingUpIds.has(p.playerId));
    return buildResult(
      `O ${teamLabel(opposingTeamId)} precisa de ${femalesToAdd.length} mulher(es).`,
      malesToMove,
      femalesToAdd,
      givingUp,
      playersToEnter,
    );
  }

  // Caso 2: o time em quadra (oposto) tem mulher a mais.
  if (excess > 0) {
    const opposingFemales = opposingTeam.filter((p) => p.gender === 'F');
    const malesInQueue = playersWaiting.filter((p) => p.gender !== 'F' && !selectedIds.has(p.playerId));
    if (malesInQueue.length === 0) return null;

    const count = Math.min(excess, opposingFemales.length, malesInQueue.length);
    const femalesInSelected = nextTeamPlayers.filter((p) => p.gender === 'F');

    let femalesToMove;
    let malesToAdd;
    let givingUp;
    if (count === 1) {
      const best = pickBestSwap(opposingFemales, malesInQueue, femalesInSelected, base);
      femalesToMove = [best.o];
      malesToAdd = [best.i];
      givingUp = best.g ? [best.g] : [];
    } else {
      femalesToMove = opposingFemales.slice(-count);
      malesToAdd = malesInQueue.slice(0, count);
      givingUp = femalesInSelected.slice(-count);
    }

    const givingUpIds = new Set(givingUp.map((p) => p.playerId));
    const playersToEnter = nextTeamPlayers.filter((p) => !givingUpIds.has(p.playerId));
    return buildResult(
      `O ${teamLabel(opposingTeamId)} tem ${femalesToMove.length} mulher(es) a mais.`,
      femalesToMove,
      malesToAdd,
      givingUp,
      playersToEnter,
    );
  }

  return null;
}
